/**
 * Report: Inspections Pending Invoicing
 *
 * URL: /admin/reports/inspections-pending
 *
 * Surfaces every service inspection that has NOT yet been turned into a
 * finalised (non-draft) invoice — either because no invoice exists at all
 * or because the invoice is still sitting in draft. Two view modes:
 * grouped-by-customer (default) and flat oldest-first.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList, Loader2, ChevronDown, ChevronRight, Phone, MessageCircle,
  ExternalLink, Receipt, XCircle, RotateCcw, Users, Rows3, LayoutGrid, EyeOff, Eye, AlertTriangle,
  FileDown, FileText,
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { fmtINR, formatErr, notifyErr, inputCls } from '../../../components/admin/billing/shared';
import { ReportHeader, StatCard } from './_shared';

const AGE_TIERS = [
  { min: 30, cls: 'bg-rose-100 text-rose-800 border-rose-200', label: 'critical' },
  { min: 14, cls: 'bg-amber-100 text-amber-800 border-amber-200', label: 'stale' },
  { min: 7, cls: 'bg-sky-100 text-sky-800 border-sky-200', label: 'chase' },
];

const ageBadge = (days) => {
  const tier = AGE_TIERS.find((t) => days >= t.min);
  const cls = tier ? tier.cls : 'bg-slate-100 text-slate-600 border-slate-200';
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>
      {days}d
    </span>
  );
};

const reasonBadge = (row) => {
  if (row.pending_reason === 'draft_invoice') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-amber-100 text-amber-800 border-amber-200" title={`Draft invoice ${row.linked_invoice?.number || ''}`}>
        <Receipt size={10} /> Draft {row.linked_invoice?.number || 'invoice'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-rose-100 text-rose-800 border-rose-200">
      No invoice
    </span>
  );
};

const vehicleLabel = (r) => [r.vehicle_year, r.make, r.model].filter(Boolean).join(' ') || '—';

const InspectionsPendingReport = () => {
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [view, setView] = useState('grouped'); // 'grouped' | 'flat'
  const [includeFlagged, setIncludeFlagged] = useState(false);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [openCust, setOpenCust] = useState({}); // { [customer_key]: bool }
  const [busy, setBusy] = useState({}); // per-row action lock
  const [downloading, setDownloading] = useState(''); // '' | 'csv' | 'pdf'

  const download = useCallback(async (kind /* 'csv' | 'pdf' */) => {
    setDownloading(kind);
    try {
      const params = new URLSearchParams();
      if (includeFlagged) params.set('include_no_invoice_flagged', 'true');
      // Send the current search string so the export matches what the
      // user sees on screen (e.g., filtering to "Lotus vinfast" should
      // only export that customer's pending inspections).
      const q = (search || '').trim();
      if (q) params.set('q', q);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const { data: blob } = await axios.get(
        `${API}/reports/inspections-pending-invoice/${kind}?${params}`,
        { headers: authHeader(), responseType: 'blob' },
      );
      const stamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 12);
      const slug = q ? `-${q.replace(/[^a-z0-9]+/gi, '-').toLowerCase().slice(0, 40)}` : '';
      const range = (dateFrom || dateTo) ? `-${dateFrom || 'any'}_to_${dateTo || 'any'}` : '';
      const filename = `inspections-pending-invoicing${slug}${range}-${stamp}.${kind}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { notifyErr(e); }
    finally { setDownloading(''); }
  }, [authHeader, includeFlagged, search, dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams();
      if (includeFlagged) params.set('include_no_invoice_flagged', 'true');
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const { data: d } = await axios.get(
        `${API}/reports/inspections-pending-invoice?${params}`,
        { headers: authHeader() },
      );
      setData(d);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, includeFlagged, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!data) return { rows: [], customers: [] };
    const q = search.trim().toLowerCase();
    if (!q) return { rows: data.rows, customers: data.customers };
    const match = (r) => [
      r.customer_name, r.contact_number, r.email, r.registration_number,
      r.make, r.model, r.serial_no, r.remarks,
    ].some((v) => String(v || '').toLowerCase().includes(q));
    const rows = (data.rows || []).filter(match);
    const customers = (data.customers || [])
      .map((g) => ({ ...g, inspections: g.inspections.filter(match) }))
      .filter((g) => g.inspections.length > 0);
    return { rows, customers };
  }, [data, search]);

  const createInvoice = async (row) => {
    if (row.pending_reason === 'draft_invoice' && row.linked_invoice?.id) {
      navigate(`/admin/billing/invoices/${row.linked_invoice.id}`);
      return;
    }
    if (!row.customer_id) {
      alert('This inspection has no customer selected. Open it and pick a customer before creating an invoice.');
      navigate(`/admin/inspections/${row.id}`);
      return;
    }
    setBusy((b) => ({ ...b, [row.id]: 'invoice' }));
    try {
      const { data: inv } = await axios.post(
        `${API}/inspections/${row.id}/create-invoice`,
        {},
        { headers: authHeader() },
      );
      navigate(`/admin/billing/invoices/${inv.id}`);
    } catch (e) {
      notifyErr(e);
      setBusy((b) => ({ ...b, [row.id]: undefined }));
    }
  };

  const flagNoInvoice = async (row) => {
    const flagging = !row.no_invoice_needed;
    let reason = '';
    if (flagging) {
      const raw = window.prompt('Reason for skipping invoice? (courtesy check / warranty follow-up / free service etc.)');
      if (raw === null) return; // user hit Cancel — abort the flag entirely
      reason = raw.trim();
    }
    setBusy((b) => ({ ...b, [row.id]: 'flag' }));
    try {
      await axios.patch(
        `${API}/inspections/${row.id}/no-invoice-flag`,
        { no_invoice_needed: flagging, reason },
        { headers: authHeader() },
      );
      await load();
    } catch (e) { notifyErr(e); }
    finally { setBusy((b) => ({ ...b, [row.id]: undefined })); }
  };

  const t = data?.totals || {};

  const renderRowActions = (r) => (
    <div className="flex flex-wrap items-center gap-1">
      <a
        href={`tel:${r.contact_number}`}
        title="Call customer"
        className={`p-1.5 rounded ${r.contact_number ? 'text-emerald-700 hover:bg-emerald-50' : 'text-slate-300 pointer-events-none'}`}
        data-testid={`ppi-call-${r.id}`}
      ><Phone size={14} /></a>
      <a
        href={r.contact_number ? `https://wa.me/${r.contact_number.replace(/[^\d]/g, '')}` : '#'}
        target="_blank" rel="noopener noreferrer"
        title="WhatsApp customer"
        className={`p-1.5 rounded ${r.contact_number ? 'text-emerald-700 hover:bg-emerald-50' : 'text-slate-300 pointer-events-none'}`}
        data-testid={`ppi-whatsapp-${r.id}`}
      ><MessageCircle size={14} /></a>
      <button
        onClick={() => navigate(`/admin/inspections/${r.id}`)}
        title="Open inspection"
        className="p-1.5 rounded text-slate-600 hover:bg-slate-100"
        data-testid={`ppi-open-${r.id}`}
      ><ExternalLink size={14} /></button>
      <button
        onClick={() => createInvoice(r)}
        disabled={busy[r.id] === 'invoice'}
        title={r.pending_reason === 'draft_invoice' ? 'Open the existing draft invoice' : 'Create draft invoice from this inspection'}
        className="inline-flex items-center gap-1 bg-teal-500 hover:bg-teal-600 disabled:opacity-50 text-white text-[11px] font-semibold px-2 py-1 rounded"
        data-testid={`ppi-invoice-${r.id}`}
      >
        {busy[r.id] === 'invoice' ? <Loader2 size={11} className="animate-spin" /> : <Receipt size={11} />}
        {r.pending_reason === 'draft_invoice' ? 'Open draft' : 'Create invoice'}
      </button>
      <button
        onClick={() => flagNoInvoice(r)}
        disabled={busy[r.id] === 'flag'}
        title={r.no_invoice_needed ? 'Unflag — put back into pending list' : 'Mark as not needing an invoice (courtesy / warranty)'}
        className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded border ${
          r.no_invoice_needed
            ? 'border-slate-300 text-slate-600 hover:bg-slate-100'
            : 'border-rose-200 text-rose-700 hover:bg-rose-50'
        }`}
        data-testid={`ppi-flag-${r.id}`}
      >
        {busy[r.id] === 'flag'
          ? <Loader2 size={11} className="animate-spin" />
          : (r.no_invoice_needed ? <RotateCcw size={11} /> : <XCircle size={11} />)}
        {r.no_invoice_needed ? 'Restore' : 'No invoice'}
      </button>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 space-y-4 bg-slate-50 min-h-full" data-testid="report-inspections-pending">
      <ReportHeader icon={ClipboardList} title="Inspections Pending Invoicing" />
      <p className="text-sm text-slate-600 max-w-3xl">
        Every service inspection that has NOT yet been turned into a finalised invoice — either it has no invoice at all
        or it&rsquo;s still parked in a draft. Great for a Monday-morning chase list.
      </p>

      {/* Controls */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
          <button
            onClick={() => setView('grouped')}
            className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm ${view === 'grouped' ? 'bg-teal-500 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
            data-testid="ppi-view-grouped"
          ><Users size={14} /> By customer</button>
          <button
            onClick={() => setView('flat')}
            className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm border-l border-slate-200 ${view === 'flat' ? 'bg-teal-500 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
            data-testid="ppi-view-flat"
          ><Rows3 size={14} /> Oldest first</button>
        </div>
        <input
          type="text"
          placeholder="Search customer, phone, plate, vehicle, remarks…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`${inputCls} flex-1 min-w-[240px]`}
          data-testid="ppi-search"
        />
        <div className="inline-flex items-center gap-1 text-xs text-slate-600">
          <label className="text-slate-500" htmlFor="ppi-date-from">From</label>
          <input
            id="ppi-date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className={`${inputCls} py-1 text-xs`}
            data-testid="ppi-date-from"
            title="Filter inspections dated on or after this date"
          />
          <label className="text-slate-500 ml-1" htmlFor="ppi-date-to">To</label>
          <input
            id="ppi-date-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className={`${inputCls} py-1 text-xs`}
            data-testid="ppi-date-to"
            title="Filter inspections dated on or before this date"
          />
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="text-slate-500 hover:text-rose-600 text-[11px] font-semibold ml-1"
              data-testid="ppi-date-clear"
              title="Clear date range"
            >Clear</button>
          )}
        </div>
        <button
          onClick={() => setIncludeFlagged((v) => !v)}
          className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1.5 rounded border ${includeFlagged ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
          data-testid="ppi-toggle-flagged"
          title="Include inspections marked as not needing an invoice"
        >
          {includeFlagged ? <Eye size={12} /> : <EyeOff size={12} />}
          {includeFlagged ? 'Showing skipped' : 'Hiding skipped'}
        </button>
        <button
          onClick={load}
          className="text-xs bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 font-semibold px-3 py-1.5 rounded"
          data-testid="ppi-refresh"
        >Refresh</button>
        <div className="w-px h-6 bg-slate-200 mx-1" aria-hidden />
        <button
          onClick={() => download('csv')}
          disabled={downloading !== '' || loading || !data?.rows?.length}
          className="inline-flex items-center gap-1 text-xs bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold px-3 py-1.5 rounded"
          data-testid="ppi-download-csv"
          title="Download the report as a CSV spreadsheet (opens in Excel / Google Sheets)"
        >
          {downloading === 'csv' ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
          CSV
        </button>
        <button
          onClick={() => download('pdf')}
          disabled={downloading !== '' || loading || !data?.rows?.length}
          className="inline-flex items-center gap-1 text-xs bg-rose-500 hover:bg-rose-600 disabled:opacity-50 text-white font-semibold px-3 py-1.5 rounded"
          data-testid="ppi-download-pdf"
          title="Download the report as a landscape A4 PDF (customer-wise)"
        >
          {downloading === 'pdf' ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
          PDF
        </button>
      </div>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>}

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard label="Pending" value={t.count ?? 0} color="text-teal-700" mono={false} testid="ppi-total-count" />
        <StatCard label="Customers" value={t.customers ?? 0} color="text-slate-900" mono={false} testid="ppi-total-customers" />
        <StatCard label="Est. revenue" value={fmtINR(t.estimate_total ?? 0)} color="text-emerald-700" testid="ppi-total-estimate" />
        <StatCard label="No invoice" value={t.no_invoice_count ?? 0} color="text-rose-700" mono={false} testid="ppi-total-no-invoice" />
        <StatCard label="In draft" value={t.draft_invoice_count ?? 0} color="text-amber-700" mono={false} testid="ppi-total-draft" />
        <StatCard label="Oldest" value={`${t.oldest_age_days ?? 0}d`} color="text-slate-900" mono={false} testid="ppi-total-oldest" />
      </div>

      {loading && !data ? (
        <div className="text-center py-10 text-slate-500"><Loader2 className="inline animate-spin mr-2" size={18} /> Loading…</div>
      ) : (filtered.rows.length === 0) ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-400 text-sm border-dashed" data-testid="ppi-empty">
          <ClipboardList className="mx-auto mb-2 text-slate-300" size={28} />
          Nothing pending — every inspection is either invoiced or intentionally skipped. Great job!
        </div>
      ) : view === 'grouped' ? (
        <div className="space-y-2" data-testid="ppi-grouped">
          {filtered.customers.map((g) => {
            const key = g.customer_id || `${g.customer_name}::${g.contact_number}`;
            const open = openCust[key] !== false; // default open
            return (
              <div key={key} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`ppi-customer-${key}`}>
                <button
                  onClick={() => setOpenCust((s) => ({ ...s, [key]: !open }))}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left"
                >
                  {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{g.customer_name || '(No customer)'}</div>
                    <div className="text-xs text-slate-500">{g.contact_number || '—'} {g.email ? `· ${g.email}` : ''}</div>
                  </div>
                  <span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full font-semibold">{g.count} pending</span>
                  <span className="text-sm font-mono font-bold text-teal-700">{fmtINR(g.total_estimate)}</span>
                  {g.oldest_age_days >= 14 && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700"><AlertTriangle size={11} /> {g.oldest_age_days}d old</span>
                  )}
                </button>
                {open && (
                  <div className="border-t border-slate-100">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                        <tr>
                          <th className="px-3 py-2 text-left">Inspection</th>
                          <th className="px-3 py-2 text-left">Vehicle</th>
                          <th className="px-3 py-2 text-left">Age</th>
                          <th className="px-3 py-2 text-left">Status</th>
                          <th className="px-3 py-2 text-right">Estimate</th>
                          <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {g.inspections.map((r) => (
                          <tr key={r.id} className={r.no_invoice_needed ? 'bg-slate-50 opacity-70' : ''} data-testid={`ppi-row-${r.id}`}>
                            <td className="px-3 py-2 align-top">
                              <div className="font-mono text-xs text-slate-700">{r.serial_no || r.id.slice(0, 6)}</div>
                              <div className="text-[11px] text-slate-500">{r.inspection_date || '—'}</div>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <div className="text-slate-800">{vehicleLabel(r)}</div>
                              <div className="text-[11px] text-slate-500 font-mono">{r.registration_number || '—'}</div>
                            </td>
                            <td className="px-3 py-2 align-top">{ageBadge(r.age_days)}</td>
                            <td className="px-3 py-2 align-top">
                              <div className="flex flex-wrap gap-1 items-center">
                                {reasonBadge(r)}
                                {r.no_invoice_needed && (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-slate-200 text-slate-600 border-slate-300" title={r.no_invoice_reason}>
                                    <EyeOff size={10} /> Skipped
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2 align-top text-right font-mono text-emerald-700 font-semibold">{fmtINR(r.estimate || 0)}</td>
                            <td className="px-3 py-2 align-top text-right">{renderRowActions(r)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="ppi-flat">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
              <tr>
                <th className="px-3 py-2 text-left">Age</th>
                <th className="px-3 py-2 text-left">Customer</th>
                <th className="px-3 py-2 text-left">Vehicle</th>
                <th className="px-3 py-2 text-left">Inspection</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Estimate</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.rows.map((r) => (
                <tr key={r.id} className={r.no_invoice_needed ? 'bg-slate-50 opacity-70' : ''} data-testid={`ppi-row-${r.id}`}>
                  <td className="px-3 py-2 align-top">{ageBadge(r.age_days)}</td>
                  <td className="px-3 py-2 align-top">
                    <div className="font-semibold text-slate-800">{r.customer_name || '—'}</div>
                    <div className="text-[11px] text-slate-500">{r.contact_number || '—'}</div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="text-slate-800">{vehicleLabel(r)}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{r.registration_number || '—'}</div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="font-mono text-xs text-slate-700">{r.serial_no || r.id.slice(0, 6)}</div>
                    <div className="text-[11px] text-slate-500">{r.inspection_date || '—'}</div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="flex flex-wrap gap-1 items-center">
                      {reasonBadge(r)}
                      {r.no_invoice_needed && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-slate-200 text-slate-600 border-slate-300" title={r.no_invoice_reason}>
                          <EyeOff size={10} /> Skipped
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top text-right font-mono text-emerald-700 font-semibold">{fmtINR(r.estimate || 0)}</td>
                  <td className="px-3 py-2 align-top text-right">{renderRowActions(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-slate-500">
        Rules: an inspection is &ldquo;pending&rdquo; when it has no linked invoice OR when its linked invoice is still in <b>draft</b> status.
        Use <b>No invoice</b> to hide courtesy / warranty checks from this list — you can restore them any time by toggling <LayoutGrid size={10} className="inline" /> Showing skipped.
      </p>
    </div>
  );
};

export default InspectionsPendingReport;
